import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useTranslation } from "react-i18next";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const peelCopy = keyframes`
  0%, 12% { opacity: 0.35; transform: translate(0, 0) rotate(0deg); }
  45%, 70% { opacity: 1; transform: translate(14px, -6px) rotate(6deg); }
  100% { opacity: 0.4; transform: translate(6px, -2px) rotate(2deg); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const popCheck = keyframes`
  0% { opacity: 0; transform: scale(0.6); }
  55% { opacity: 1; transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
`;

const confetti = keyframes`
  0%, 100% { opacity: 0.25; transform: translateY(0) scale(0.85); }
  50% { opacity: 1; transform: translateY(-6px) scale(1); }
`;

function DuplicatePreview({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "22%",
          top: 16,
          width: 46,
          height: 56,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          ...motionSafe,
        }}
      >
        <Box
          sx={{
            mx: 0.85,
            mt: 1.1,
            height: 5,
            borderRadius: 0.5,
            bgcolor: alpha(accent, 0.35),
          }}
        />
        <Box
          sx={{
            mx: 0.85,
            mt: 0.6,
            height: 4,
            width: "55%",
            borderRadius: 0.5,
            bgcolor: alpha(accent, 0.22),
          }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "38%",
          top: 12,
          width: 46,
          height: 56,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.55),
          boxShadow: `0 8px 18px ${alpha(accent, 0.22)}`,
          display: "grid",
          placeItems: "center",
          animation: `${peelCopy} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ContentCopyOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 14,
          bottom: 12,
          px: 0.85,
          py: 0.35,
          borderRadius: 1,
          bgcolor: alpha(accent, 0.14),
          border: "1px solid",
          borderColor: alpha(accent, 0.4),
          animation: `${bob} 2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Typography
          sx={{ fontSize: "0.58rem", fontWeight: 900, color: accent }}
        >
          COPY
        </Typography>
      </Box>
    </Box>
  );
}

function SuccessPreview({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      {[18, 42, 68].map((left, i) => (
        <Box
          key={left}
          sx={{
            position: "absolute",
            left: `${left}%`,
            top: 10 + (i % 2) * 8,
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: alpha(accent, 0.55 - i * 0.1),
            animation: `${confetti} 2.2s ease-in-out infinite`,
            animationDelay: `${i * 0.18}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 16,
          ml: "-28px",
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.16),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 20px ${alpha(accent, 0.25)}`,
          animation: `${popCheck} 480ms ease both`,
          ...motionSafe,
        }}
      >
        <CheckCircleRoundedIcon sx={{ fontSize: 34, color: accent }} />
      </Box>
    </Box>
  );
}

const paperSx = {
  borderRadius: 3,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
};

const backdropSx = {
  backdropFilter: "blur(10px)",
  backgroundColor: "rgba(15, 23, 42, 0.45)",
};

/**
 * Confirmación de duplicar presupuesto (motion Meetings) + fecha máxima.
 */
export function EstimateDuplicateBudgetDialog({
  open,
  onClose,
  onConfirm,
  busy = false,
  fechaMaxima,
  onFechaMaximaChange,
  fechaError = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.primary.main || "#1976d2";

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{ backdrop: { sx: backdropSx } }}
      PaperProps={{ sx: paperSx }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.12)} 0%, ${alpha(
              th.palette.info.main,
              0.06,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("EstimatesPage.drawer.confirmDuplicateTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {t("EstimatesPage.drawer.duplicateHint")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("EstimatesPage.cancel")}
            onClick={onClose}
            disabled={busy}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <DuplicatePreview accent={accent} />
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
            fontWeight: 600,
            mb: 1.75,
            animation: `${rise} 300ms ease both`,
            ...motionSafe,
          }}
        >
          {t("EstimatesPage.drawer.confirmDuplicateBody")}
        </Typography>

        <TextField
          fullWidth
          size="small"
          type="date"
          required
          label={t("EstimatesPage.contestacion.title")}
          InputLabelProps={{ shrink: true }}
          value={fechaMaxima || ""}
          onChange={(e) => onFechaMaximaChange?.(e.target.value || "")}
          error={fechaError}
          helperText={
            fechaError
              ? t("EstimatesPage.drawer.duplicateNeedFechaMaxima")
              : t("EstimatesPage.drawer.duplicateFechaMaximaHint")
          }
          disabled={busy}
          sx={{
            animation: `${rise} 360ms ease both`,
            ...motionSafe,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: alpha(accent, 0.03),
            },
          }}
        />
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ fontWeight: 700, minHeight: 44 }}
        >
          {t("EstimatesPage.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={onConfirm}
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ContentCopyOutlinedIcon />
            )
          }
          sx={{
            minHeight: 48,
            fontWeight: 800,
            borderRadius: 2,
            boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
          }}
        >
          {busy
            ? t("EstimatesPage.drawer.duplicating")
            : t("EstimatesPage.drawer.duplicate")}
        </Button>
      </Stack>
    </Dialog>
  );
}

/**
 * Éxito al duplicar presupuesto (motion Meetings).
 */
export function EstimateDuplicateSuccessDialog({ open, onClose, nombre }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.success.main || "#2e7d32";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{ backdrop: { sx: backdropSx } }}
      PaperProps={{ sx: paperSx }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.success.main, 0.14)} 0%, ${alpha(
              th.palette.primary.main,
              0.06,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("EstimatesPage.drawer.duplicateSuccessTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {t("EstimatesPage.drawer.duplicateSuccessHint")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("EstimatesPage.cancel")}
            onClick={onClose}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <SuccessPreview accent={accent} />
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
            fontWeight: 600,
            animation: `${rise} 300ms ease both`,
            ...motionSafe,
          }}
        >
          {nombre
            ? t("EstimatesPage.drawer.duplicateSuccessBodyNamed", { nombre })
            : t("EstimatesPage.drawer.duplicateSuccessBody")}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={onClose}
          startIcon={<CheckCircleRoundedIcon />}
          sx={{
            minHeight: 48,
            fontWeight: 800,
            borderRadius: 2,
            boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
          }}
        >
          {t("EstimatesPage.drawer.duplicateSuccessOk")}
        </Button>
      </Stack>
    </Dialog>
  );
}
